import { Text } from "@clipboard-health/ui-react";
import {
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Collapse,
  Container,
  Divider,
  Link,
  Stack,
} from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { formatInTimeZone } from "date-fns-tz";
import { useState } from "react";

import { CbhIcon } from "../components/CbhIcon/CbhIcon";
import { SUPPORT_TELEPHONE } from "../ContactCBHInfo/constants";
import { Avatar } from "../lib/Avatar";
import { useIsSmallScreen } from "./hooks/useIsSmallScreen";
import { useShiftBoardData } from "./hooks/useShiftBoardData";
import { ShiftBoardShiftCardDetails } from "./ShiftCard/Details";
import { ShiftBoardEtaMessage } from "./ShiftCard/EtaMessage";
import { ShiftBoardShiftStatusChip } from "./ShiftCard/ShiftStatusChip";
import { ShiftCardProvider, useShiftCardContext } from "./ShiftCard/useShiftCard";
import { type DateTimeSlot, type ShiftBoardWorkplace } from "./types";
import { convertDateTimeSlotToDateInterval } from "./util/convert";
import { filterAndSortShifts } from "./util/shiftUtils";

function ShiftBoardShiftCard() {
  const { shift, workplace, isUrgentShift, isUrgentBookable } = useShiftCardContext();
  const [showDetails, setShowDetails] = useState(false);
  const isSmallScreen = useIsSmallScreen();

  const name = shift.worker?.name ?? "Unfilled";
  const isOpenAndUrgent = isUrgentShift && isUrgentBookable;

  return (
    <Card variant="outlined" sx={{ borderColor: isOpenAndUrgent ? "#5E4300" : "undefined" }}>
      <CardActionArea
        onClick={() => {
          const newShowDetails = !showDetails;
          setShowDetails(newShowDetails);

          if (newShowDetails) {
            logEvent(APP_V2_USER_EVENTS.SHIFT_BOARD_VIEW_DETAILS_CLICKED, {
              shiftId: shift.id,
              workerId: shift.worker?.id,
              qualification: shift.qualification,
            });
          }
        }}
      >
        <CardHeader
          disableTypography
          title={
            <Stack gap={2} width="100%">
              <Stack direction="column" gap={2}>
                <Stack direction="row" gap={1} alignItems="center" width="100%">
                  <Avatar variant="rounded" name={name} src={shift.worker?.avatarUrl} />
                  <Stack overflow="hidden" width="100%">
                    <Stack direction="row" gap={1} alignItems="center" overflow="hidden">
                      <Text
                        variant="h4"
                        sx={{ overflow: "hidden", textWrap: "nowrap", textOverflow: "ellipsis" }}
                      >
                        {name}
                      </Text>
                      <Text variant="body2">{shift.qualification}</Text>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Stack direction="row" gap={1} alignItems="center">
                        <Text>{`${formatInTimeZone(
                          shift.start,
                          workplace.timeZone,
                          "h:mm aa"
                        )} - ${formatInTimeZone(shift.end, workplace.timeZone, "h:mm aa")}`}</Text>
                        {isOpenAndUrgent && !isSmallScreen && (
                          <Text variant="body2" color="#5E4300">
                            Urgent shift created
                          </Text>
                        )}
                      </Stack>
                      {isSmallScreen ? (
                        <Stack direction="row" gap={0.5}>
                          <ShiftBoardShiftStatusChip />
                        </Stack>
                      ) : null}
                    </Stack>
                  </Stack>
                  {isSmallScreen ? null : (
                    <Stack direction="column" gap={1} alignItems="end">
                      <Stack direction="row" gap={0.5}>
                        <ShiftBoardShiftStatusChip />
                      </Stack>
                      <Stack direction="row" gap={1} alignItems="center">
                        <CbhIcon type={showDetails ? "chevron-up" : "chevron-down"} size="small" />
                        <Text noWrap>{showDetails ? "Hide details" : "View details"}</Text>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
                <ShiftBoardEtaMessage width="100%" />
                {isSmallScreen ? (
                  <Stack direction="row" justifyContent="space-between" gap={1} alignItems="end">
                    <Stack direction="row" gap={1} alignItems="center">
                      <CbhIcon type={showDetails ? "chevron-up" : "chevron-down"} size="small" />
                      <Text>{showDetails ? "Hide details" : "View details"}</Text>
                    </Stack>
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          }
          border={1}
          sx={{
            "& .MuiCardHeader-content": {
              maxWidth: "100%",
            },
          }}
        />
        <Divider />
        <Collapse in={showDetails}>
          <CardContent>
            <ShiftBoardShiftCardDetails logs={shift.logs} workplace={workplace} />
          </CardContent>
        </Collapse>
      </CardActionArea>
    </Card>
  );
}

export function ShiftBoardContent(props: {
  token: string;
  dateTimeSlot: DateTimeSlot;
  workplace: ShiftBoardWorkplace;
  maxWidth?: number;
}) {
  const { workplace, token, dateTimeSlot, maxWidth } = props;
  const isSmallScreen = useIsSmallScreen();
  const { shifts } = useShiftBoardData({
    token,
    dateInterval: convertDateTimeSlotToDateInterval(dateTimeSlot, workplace.timeZone),
  });
  const sortedShifts = filterAndSortShifts(shifts, dateTimeSlot.timeSlot);

  return (
    <CardContent sx={{ backgroundColor: "#FAFAFA", overflow: "auto" }}>
      <Container sx={{ maxWidth }} disableGutters={isSmallScreen}>
        <Stack sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {sortedShifts.length === 0 ? (
            <Stack justifyContent="center" alignItems="center" height="100%">
              <Text variant="h4" sx={{ color: (theme) => theme.palette.text.secondary }}>
                No {dateTimeSlot.timeSlot.toUpperCase()} shifts were posted for{" "}
                {formatInTimeZone(dateTimeSlot.date, workplace.timeZone, "MM/dd")}
              </Text>
            </Stack>
          ) : (
            sortedShifts.map((shift) => (
              <ShiftCardProvider key={shift.id} shift={shift} workplace={workplace}>
                <ShiftBoardShiftCard />
              </ShiftCardProvider>
            ))
          )}
          {isSmallScreen && (
            <Card sx={{ marginTop: 2, backgroundColor: "white" }}>
              <CardContent sx={{ textAlign: "center", padding: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
                  <Text variant="body2">Need help? Call us:</Text>
                  <Link href={`tel:${SUPPORT_TELEPHONE}`} underline="none">
                    <Text variant="body2" sx={{ color: "primary.main", fontWeight: "bold" }}>
                      {SUPPORT_TELEPHONE}
                    </Text>
                  </Link>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </CardContent>
  );
}
